import { useId, useMemo, useState } from 'react'
import { DownOutlined, FileTextOutlined, InfoCircleOutlined } from '../../../components/icons'
import {
  CONFIG_PROCESS_OPTIONS,
  useManufacturingConfigCascade,
} from '../../../components/manufacturing-config-cascade'
import { Button } from '../../../components/ui/Button'
import { Modal } from '../../../components/ui/Modal'
import { readNumberAttr, readStringAttr, readStringListAttr } from '../config-attrs'
import type { EditPartConfigRequest, PartProcess } from '../edit-part-config-service'
import { formatPrice } from '../format'
import { normalizeProcess } from '../process-normalization'
import type { LibraryPartRow } from '../types'

export interface PartsBulkConfigureModalProps {
  readonly open: boolean
  readonly onClose: () => void
  /** The selected rows this configuration will be applied to. Its length
   * drives the "Configure N parts" title/CTA count; the rows themselves
   * feed the mixed-existing-values advisory (AC3) and the aggregate price
   * preview (AC2) — both computed CLIENT-SIDE from data already loaded for
   * the Library listing, not a second fetch. */
  readonly parts: readonly LibraryPartRow[]
  /** Applies the collected configuration to every selected part. The caller
   * owns the loop-PATCH + partial-failure accounting + re-price refetch
   * (`bulk-configure-service` / `PartsLibraryPage`); this modal only gathers
   * the shared config and hands it over. Resolving closes the modal. */
  readonly onSubmit: (request: EditPartConfigRequest) => Promise<void>
}

/** UID-93: same real, ordered process taxonomy `EditConfigurationModal` uses
 * (instant/manual-quote processes first, then the 48-hour-manual-quote
 * group) — never a second, hand-typed process list.
 *
 * UID-346 (ADR-0117 D9) — re-exported from the shared cascade module so this
 * modal's JSX (which iterates `PROCESS_OPTIONS` under this established local
 * name) is untouched by the extraction. */
const PROCESS_OPTIONS = CONFIG_PROCESS_OPTIONS

const QUANTITY_MIN = 1
const QUANTITY_MAX = 1_000_000

/** AC3 — "Selected parts currently have different {Field} values" advisory
 * field labels, in display order. Only fields this modal actually
 * overwrites unconditionally (process/material/finish/inspections/
 * certificates — this modal always PATCHes a full configuration, per its
 * own "every selected part will be updated" framing) are eligible; quantity
 * is checked only when the caller has actually typed one in (a blank
 * quantity is never sent — see `PartsBulkConfigureModalProps.onSubmit`'s doc
 * comment on `EditPartConfigRequest.quantity`). */
type AdvisoryField = 'Process' | 'Material' | 'Finish' | 'Quantity' | 'Inspections' | 'Certificates'

function isMixed<T>(rows: readonly LibraryPartRow[], extract: (row: LibraryPartRow) => T): boolean {
  if (rows.length < 2) return false
  const values = new Set(rows.map((row) => JSON.stringify(extract(row))))
  return values.size > 1
}

function joinFieldNames(labels: readonly string[]): string {
  if (labels.length === 0) return ''
  if (labels.length === 1) return labels[0] ?? ''
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`
}

/** AC2 — a coarse, client-side aggregate estimate (NOT a per-part re-price
 * curve — no BFF call this modal could re-price against exists) averaging
 * the `indicativePrice` of every SELECTED row that is already `auto_priced`
 * today. `null` when none of the selection has a numeric indicative price
 * to average, so the price-preview footer falls back to a no-figure
 * message rather than fabricating a "$0.00". */
function estimateUnitPrice(
  parts: readonly LibraryPartRow[],
): { readonly amount: number; readonly currency: string } | null {
  const priced = parts.filter(
    (part) =>
      part.sourceable.pricingProjection?.mode === 'auto_priced' && part.sourceable.indicativePrice,
  )
  if (priced.length === 0) return null
  const currency = priced[0]?.sourceable.indicativePrice?.currency ?? 'USD'
  // Refuse a mixed-currency sum (mirrors main's partProductionSubtotal fix,
  // f9d45e37): averaging amounts across currencies and labelling the result
  // with just the first row's currency would fabricate a meaningless figure.
  // Fall back to the no-figure message instead.
  if (priced.some((part) => part.sourceable.indicativePrice?.currency !== currency)) {
    return null
  }
  const total = priced.reduce((sum, part) => {
    const amount = Number.parseFloat(part.sourceable.indicativePrice?.amount ?? '0')
    return sum + (Number.isFinite(amount) ? amount : 0)
  }, 0)
  return { amount: total / priced.length, currency }
}

/**
 * UID-46/UID-257 — the Library selection bar's "Configure N" bulk-
 * configuration workflow (design handoff §8, `bulkView` dc.html ~5731-5822),
 * rebuilt for parity with the row-level `EditConfigurationModal`: the REAL
 * process/material/finish cascade (`process-material-config.ts`, same
 * source that modal reads — never a second, hand-typed shortlist), material-
 * gated Inspections/Certificates with the same helper text, an in-modal
 * aggregate price preview, and a non-blocking advisory when the selected
 * parts don't all currently share the same value for a field this modal is
 * about to overwrite. No unsaved-changes guard — backdrop/Escape/×/Cancel
 * discard immediately, same as every other `Modal` caller.
 */
export function PartsBulkConfigureModal({
  open,
  onClose,
  parts,
  onSubmit,
}: PartsBulkConfigureModalProps) {
  const partCount = parts.length
  const [busy, setBusy] = useState(false)

  // UID-346 (ADR-0117 D9) — the cascade state EXTRACTED into the shared hook;
  // see that module's doc comment for what is/isn't shared with the RFQ
  // vertical's Bulk configure modal and why. Local names below are kept
  // identical to what this file used before extraction so the rest of this
  // component (JSX, `handleSubmit`, `advisoryText`) needed no further edits.
  const cascade = useManufacturingConfigCascade(PROCESS_OPTIONS[0]?.id ?? 'cnc_machining')
  const {
    process,
    material,
    finish,
    inspections,
    certificates,
    quantityText,
    setQuantityText,
    materialsForProcess,
    materialChosen,
    availableFinishes,
    finishChosen,
    certificatesAllowed,
    availableCertificates,
    availableInspections,
    ready,
    toggleInspection,
    toggleCertificate,
  } = cascade
  const setProcess = cascade.setProcess
  const setFinish = cascade.setFinish

  const processId = useId()
  const materialId = useId()
  const finishId = useId()
  const quantityId = useId()
  const inspectionsHeadingId = useId()
  const certificatesHeadingId = useId()
  // UID-584 — the pre-material Certificates helper text needs a stable id so
  // the disabled placeholder select's `aria-describedby` can point at it
  // (the reason it is disabled), not just its `aria-labelledby` name.
  const certificatesHelpId = useId()

  const rfqForced = inspections.length > 0 || certificates.length > 0
  const priceState: 'notReady' | 'priced' | 'rfq' = !ready
    ? 'notReady'
    : rfqForced
      ? 'rfq'
      : 'priced'

  /** AC2's bracketed ", a few may route to RFQ" — shown only alongside the
   * `priced` state, when at least one selected part is NOT already
   * `auto_priced` today (so applying this shared config to it may not
   * auto-price it either). A coarse aggregate signal, not a per-part
   * prediction. */
  const someAlreadyNeedQuote = parts.some(
    (part) =>
      part.sourceable.pricingProjection?.mode === 'quote_required' ||
      part.sourceable.pricingProjection?.mode === 'locked_to_rfq',
  )

  function handleProcessChange(next: PartProcess) {
    setProcess(next)
  }

  function handleMaterialChange(next: string) {
    cascade.setMaterial(next)
  }

  const advisoryText = useMemo(() => {
    const mixed: AdvisoryField[] = []
    if (
      isMixed(parts, (row) =>
        normalizeProcess(readStringAttr(row.sourceable.configAttributes, 'process') || undefined),
      )
    ) {
      mixed.push('Process')
    }
    if (isMixed(parts, (row) => readStringAttr(row.sourceable.configAttributes, 'material'))) {
      mixed.push('Material')
    }
    if (isMixed(parts, (row) => readStringAttr(row.sourceable.configAttributes, 'finish'))) {
      mixed.push('Finish')
    }
    if (
      quantityText.trim() &&
      isMixed(
        parts,
        (row) =>
          readNumberAttr(row.sourceable.configAttributes, 'quantity') ?? row.quantity ?? null,
      )
    ) {
      mixed.push('Quantity')
    }
    if (
      isMixed(parts, (row) =>
        [...readStringListAttr(row.sourceable.configAttributes, 'inspections')].sort(),
      )
    ) {
      mixed.push('Inspections')
    }
    if (
      isMixed(parts, (row) =>
        [...readStringListAttr(row.sourceable.configAttributes, 'certificates')].sort(),
      )
    ) {
      mixed.push('Certificates')
    }
    if (mixed.length === 0) return null
    return `Selected parts currently have different ${joinFieldNames(mixed)} values — this will replace all of them.`
  }, [parts, quantityText])

  async function handleSubmit() {
    setBusy(true)
    try {
      const request: EditPartConfigRequest = {
        process,
        material,
        finish,
        // This modal has no color picker; applying a new finish across the set
        // must CLEAR each part's stale color (a color belongs to a specific
        // finish) rather than leave it orphaned. Mirrors the single-part
        // modal, which sends color:null whenever finish is set without a
        // matching color (EditConfigurationModal buildEditRequest).
        color: null,
        inspections: inspections.length > 0 ? [...inspections] : null,
        certificates: materialChosen && certificates.length > 0 ? [...certificates] : null,
        ...(quantityText.trim()
          ? {
              quantity: Math.min(
                QUANTITY_MAX,
                Math.max(QUANTITY_MIN, Math.round(Number(quantityText)) || QUANTITY_MIN),
              ),
            }
          : {}),
      }
      await onSubmit(request)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Configure ${partCount} part${partCount === 1 ? '' : 's'}`}
      /* UID-584 — shared footer treatment for the two bulk-configure modals
         (narrower primary button per the design, and buttons that wrap rather
         than clip their nowrap labels at narrow widths). Defined once as
         `.modal-bulk-configure` in ui.css. */
      className="modal-bulk-configure"
      footer={
        <>
          {/* UID-584 — Cancel reads as the bordered/filled secondary button
              (design fidelity), not the plain-text link variant.

              `size="lg"` is the 40px step (`--control-height-lg`). The
              design has ONE bulk-configure view (`Fictiv Platform.dc.html`
              `bulkView`, :10305) whose `saveLabel` merely swaps on an `rfq`
              flag (:10318) — this app split it into two components, so both
              map to the same footer geometry: Cancel `height:40px;padding:0
              16px` (:5821) and Save `height:40px;padding:0 20px` (:10304).
              That also matches the 40px `.part-config-select` dropdowns in
              the body; the default `md` step is 32px. */}
          <Button variant="secondary" size="lg" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button size="lg" onClick={handleSubmit} loading={busy} disabled={!ready}>
            {`Apply to ${partCount} part${partCount === 1 ? '' : 's'}`}
          </Button>
        </>
      }
    >
      {advisoryText && (
        // `<output>` carries an implicit `status` ARIA role (WAI-ARIA), so a
        // screen reader announces this non-blocking advisory the same way an
        // explicit `role="status"` would, without the redundant attribute.
        <output className="caption-text bulk-configure-advisory">{advisoryText}</output>
      )}

      <div className="part-config-fields">
        <div className="part-config-field">
          <label htmlFor={processId} className="part-config-field-label">
            Process
          </label>
          <div className="part-config-select-wrap">
            <select
              id={processId}
              className="part-config-select"
              value={process}
              onChange={(event) => handleProcessChange(event.target.value as PartProcess)}
            >
              {PROCESS_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            <DownOutlined aria-hidden="true" className="part-config-select-chevron" />
          </div>
        </div>

        <div className="part-config-field">
          <label htmlFor={materialId} className="part-config-field-label">
            Material
          </label>
          <div className="part-config-select-wrap">
            <select
              id={materialId}
              className="part-config-select"
              value={material}
              onChange={(event) => handleMaterialChange(event.target.value)}
            >
              <option value="">Select material…</option>
              {materialsForProcess.map((option) => (
                <option key={option.id} value={option.name}>
                  {option.name}
                </option>
              ))}
            </select>
            <DownOutlined aria-hidden="true" className="part-config-select-chevron" />
          </div>
        </div>

        <div className="part-config-field">
          <label htmlFor={finishId} className="part-config-field-label">
            Finish
          </label>
          <div className="part-config-select-wrap">
            <select
              id={finishId}
              className="part-config-select"
              value={finish}
              disabled={!materialChosen}
              onChange={(event) => setFinish(event.target.value)}
            >
              <option value="">Select finish…</option>
              {availableFinishes.map((option) => (
                <option key={option.id} value={option.name}>
                  {option.name}
                </option>
              ))}
            </select>
            <DownOutlined aria-hidden="true" className="part-config-select-chevron" />
          </div>
          {!materialChosen && (
            <span className="caption-text">Select a material to see finish options.</span>
          )}
        </div>

        <fieldset className="part-config-section part-config-fieldset">
          <legend id={inspectionsHeadingId} className="part-config-field-label">
            Inspections <span className="caption-text">(optional)</span>
          </legend>
          {!materialChosen ? (
            <p className="caption-text">Select a material to see available inspections.</p>
          ) : availableInspections.length === 0 ? (
            <p className="caption-text">No inspections available for this material.</p>
          ) : (
            availableInspections.map((option) => (
              <label key={option} className="part-config-checkbox-row">
                <input
                  type="checkbox"
                  checked={inspections.includes(option)}
                  onChange={() => toggleInspection(option)}
                />
                {option}
              </label>
            ))
          )}
        </fieldset>

        <fieldset className="part-config-section part-config-fieldset">
          <legend id={certificatesHeadingId} className="part-config-field-label">
            Certificates <span className="caption-text">(optional)</span>
          </legend>
          {!materialChosen ? (
            <>
              <p id={certificatesHelpId} className="caption-text">
                Select a material to see available certificates.
              </p>
              {/* UID-584 — a disabled placeholder control alongside (not
                  replacing) the helper text above, matching the
                  Process/Material/Finish `.part-config-select` chrome
                  everywhere else in this modal instead of leaving
                  Certificates as the only field with no control at all
                  before a material is chosen. `aria-labelledby` points at
                  the `<legend>` above since a disabled `<select>` still
                  needs an accessible name; `aria-describedby` points at the
                  helper text so a screen reader also gets the REASON it is
                  unavailable, which "disabled" alone does not convey. */}
              <div className="part-config-select-wrap">
                <select
                  className="part-config-select"
                  disabled
                  aria-labelledby={certificatesHeadingId}
                  aria-describedby={certificatesHelpId}
                >
                  <option value="">Select certificates...</option>
                </select>
                <DownOutlined aria-hidden="true" className="part-config-select-chevron" />
              </div>
            </>
          ) : !certificatesAllowed ? (
            <p className="caption-text">Certificates aren't offered for this process.</p>
          ) : availableCertificates.length === 0 ? (
            <p className="caption-text">No certificates available for this material.</p>
          ) : (
            availableCertificates.map((option) => (
              <label key={option} className="part-config-checkbox-row">
                <input
                  type="checkbox"
                  checked={certificates.includes(option)}
                  onChange={() => toggleCertificate(option)}
                />
                {option}
              </label>
            ))
          )}
        </fieldset>

        <div className="part-config-field part-config-field-quantity">
          <label htmlFor={quantityId} className="part-config-field-label">
            Quantity <span className="caption-text">(optional)</span>
          </label>
          <input
            id={quantityId}
            type="number"
            min={QUANTITY_MIN}
            max={QUANTITY_MAX}
            step={1}
            placeholder="Keep each part's qty"
            value={quantityText}
            onChange={(event) => setQuantityText(event.target.value)}
          />
        </div>
      </div>

      {/* AC2 — in-modal aggregate price preview, mutually exclusive with
          `notReady`/`priced`/`rfq`, same 3-state shape (and reused
          `.part-config-pricing-callout*` styling) as
          `EditConfigurationModal`'s `ConfigPricingFooter`. */}
      {priceState === 'notReady' && (
        <div className="part-config-pricing-callout part-config-pricing-callout-info">
          <InfoCircleOutlined aria-hidden="true" />
          <p>Choose a material and finish to apply.</p>
        </div>
      )}
      {priceState === 'rfq' && (
        <div className="part-config-pricing-callout part-config-pricing-callout-violet">
          <FileTextOutlined aria-hidden="true" />
          <p>
            Because you selected custom inspections or certificates, all parts will be submitted for
            a manual quote.
          </p>
        </div>
      )}
      {priceState === 'priced' &&
        (() => {
          const estimate = estimateUnitPrice(parts)
          return (
            <div className="part-config-pricing-callout part-config-pricing-callout-info">
              <InfoCircleOutlined aria-hidden="true" />
              <p>
                {estimate ? (
                  <>
                    Est.{' '}
                    <b>
                      {formatPrice({
                        amount: estimate.amount.toFixed(2),
                        currency: estimate.currency,
                      })}
                    </b>{' '}
                    / part · most parts auto-price
                    {someAlreadyNeedQuote ? ', a few may route to RFQ' : ''}.
                  </>
                ) : (
                  <>
                    Most parts will auto-price once applied
                    {someAlreadyNeedQuote ? ', a few may route to RFQ' : ''}.
                  </>
                )}
              </p>
            </div>
          )
        })()}
    </Modal>
  )
}
